import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

import Hero from '../components/Hero';
import Services from '../components/Services';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Award, Users, Building, Star } from 'lucide-react';
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from "@/components/ui/carousel";
import { portfolioItems } from '../data/portfolioItems';

const HomePage = () => {
  // Function to get featured projects in random order
  const getFeaturedProjects = () => {
    // Define the exact titles of projects you want to feature
    const featuredTitles = [
      "Deep Dive Dubai",
      "Sharjah Calligraphy Museum",
      "فالق الثمامة - Thumama Fault",
      "Wow General Trading LLC",
      "Sri Lanka Planetarium",
      "98 Acres Resort & Spa",
      "Bombay Borough",
      "Ajam Specialty Coffee"
    ];
    
    // Get the corresponding projects by title
    const featuredProjects = featuredTitles.map(title => 
      portfolioItems.find(item => item.title === title)
    ).filter(Boolean);
    
    // Shuffle the array to randomize order on each page refresh
    return [...featuredProjects].sort(() => Math.random() - 0.5);
  };

  const randomizedFeaturedProjects = React.useMemo(getFeaturedProjects, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Samsons Enterprises | 360° Virtual Tour Services</title>
        <meta name="description" content="Explore stunning 360° virtual tours by Samsons Enterprises FZC, a leading Google-trusted virtual tour agency in UAE, Sri Lanka and Saudi Arabia." />
        <link rel="canonical" href="https://samsons.ae/" />
        <meta property="og:title" content="Samsons Enterprises | 360° Virtual Tour Services" />
        <meta property="og:description" content="Explore stunning 360° virtual tours by Samsons Enterprises FZC, a leading Google-trusted virtual tour agency in UAE, Sri Lanka and Saudi Arabia." />
        <meta property="og:url" content="https://samsons.ae/" />
      </Helmet>
      <main>
      <Hero />
      
      
      {/* Features Section */}
      <section id="features" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-vista-blue">Why Choose Our Virtual Tours?</h2>
            <p className="text-vista-dark-gray max-w-3xl mx-auto">
              Our immersive 360° virtual tours provide a cutting-edge way to showcase any space, 
              driving engagement and bringing your location to life.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard 
              icon={<MapPin className="h-8 w-8 text-vista-orange" />}
              title="Google Maps Integration"
              description="Increase visibility on Google Maps with fully integrated virtual tours accessible to millions."
            />
            <FeatureCard 
              icon={<Award className="h-8 w-8 text-vista-orange" />}
              title="Premium Quality"
              description="Crystal-clear high-resolution imagery captures every detail of your space."
            />
            <FeatureCard 
              icon={<Users className="h-8 w-8 text-vista-orange" />}
              title="Customer Engagement"
              description="Boost customer confidence with interactive virtual walk-throughs of your business."
            />
            <FeatureCard 
              icon={<Building className="h-8 w-8 text-vista-orange" />}
              title="Versatile Solutions"
              description="From hotels to retail stores, our tours work for any industry or space."
            />
          </div>

          <div className="mt-12 text-center">
            <Link to="/services">
              <Button className="bg-vista-blue hover:bg-vista-blue/90 text-white px-8 py-6">
                Explore Our Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      
      {/* Featured Projects */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-vista-blue text-center">Featured Projects</h2>
            <p className="text-vista-dark-gray max-w-3xl mx-auto text-center">
              Take a look at some of our recent virtual tour projects across UAE, Sri Lanka and Saudi Arabia
            </p>
          </div>
          
          <Carousel className="max-w-6xl mx-auto" opts={{ align: "start", loop: true }}>
            <CarouselContent className="-ml-2 md:-ml-4">
              {randomizedFeaturedProjects.map((project) => (
                <CarouselItem key={project.id} className="pl-2 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                  <ProjectCard 
                    image={project.thumbnailUrl}
                    title={project.title}
                    category={project.category}
                    location={`${project.location}, ${project.country}`}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-0 lg:-left-12" aria-label="Previous featured project" />
            <CarouselNext className="right-0 lg:-right-12" aria-label="Next featured project" />
          </Carousel>
          
          <div className="mt-12 text-center">
            <Link to="/portfolio">
              <Button variant="outline" className="px-8 py-6">
                View All Projects
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Testimonials */}
      <section className="py-16 bg-gradient-to-r from-vista-soft-green to-vista-soft-lime">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-vista-blue">What Our Clients Say</h2>
            <p className="text-vista-dark-gray max-w-3xl mx-auto">
              Hear from businesses that have transformed their online presence with our virtual tour services
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <TestimonialCard 
              quote="The virtual tour created by Samson's team has significantly increased customer engagement and bookings for our resort."
              author="Resort Manager"
              company="98 Acres Resort & Spa"
              rating={5}
            />
            <TestimonialCard 
              quote="Our customers love being able to explore our space virtually before visiting. It's been a game-changer for our business."
              author="Business Owner"
              company="Garton's Ark Restaurant"
              rating={5}
            />
            <TestimonialCard 
              quote="Professional service and outstanding quality. The virtual tour perfectly captures the atmosphere of our venue."
              author="Marketing Director"
              company="Rosewood Ceylon"
              rating={5}
            />
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-vista-blue">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Ready to Transform Your Business?</h2>
          <p className="text-white text-xl mb-8 max-w-2xl mx-auto">
            Get in touch today to discuss how our virtual tour solutions can elevate your business presence.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-vista-blue hover:bg-vista-off-white px-8 py-6 text-lg">
              Contact Us Today
            </Button>
          </Link>
        </div>
      </section>
      </main>
      
      <Footer />
    </div>
  );
};

// Helper Components
const FeatureCard = ({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) => (
  <Card className="hover:shadow-lg transition-all duration-300 h-full border-t-2 border-vista-blue">
    <CardHeader className="flex flex-col items-center pb-2">
      <div className="mb-4 p-3 bg-vista-soft-green rounded-full">{icon}</div>
      <h3 className="font-bold text-xl text-vista-blue text-center">{title}</h3>
    </CardHeader>
    <CardContent>
      <p className="text-center text-vista-dark-gray">{description}</p>
    </CardContent>
  </Card>
);

const ProjectCard = ({ image, title, category, location }: { image: string, title: string, category: string, location: string }) => (
  <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 h-full">
    <div className="h-48 overflow-hidden">
      <img 
        src={image} 
        alt={title} 
        className="w-full h-full object-cover transition-transform hover:scale-105 duration-300"
      />
    </div>
    <CardContent className="p-4">
      <Badge variant="lime" className="mb-2">{category}</Badge>
      <h3 className="font-bold text-lg mb-1">{title}</h3>
      <p className="text-sm text-vista-dark-gray flex items-center">
        <MapPin className="h-4 w-4 mr-1 inline" /> {location}
      </p>
    </CardContent>
  </Card>
);

const TestimonialCard = ({ quote, author, company, rating }: { quote: string, author: string, company: string, rating: number }) => (
  <Card className="h-full hover:shadow-lg transition-all duration-300">
    <CardContent className="p-6">
      <div className="flex mb-4">
        {[...Array(rating)].map((_, i) => (
          <Star key={i} className="h-5 w-5 fill-current text-vista-orange" />
        ))}
      </div>
      <p className="italic text-vista-dark-gray mb-4">"{quote}"</p>
      <div>
        <p className="font-bold text-vista-blue">{author}</p>
        <p className="text-sm text-vista-dark-gray">{company}</p>
      </div>
    </CardContent>
  </Card>
);

export default HomePage;
